/**
 * Every destination this extension can send a creative to.
 *
 * Two kinds sit behind one interface. Ad platforms turn a creative into an ad
 * asset and enforce their own specs. DAM systems store it, and are the set
 * already available as GraFx media connectors, so a customer who browses assets
 * from one inside Studio can push finished creatives back to the same system.
 *
 * `demo_dam` is a stand-in used to demonstrate the DAM path end to end while
 * the real connectors are stubs.
 */
export const PLATFORMS = [
  'google_ads',
  'meta',
  'trade_desk',
  'acquia_dam',
  'adobe_experience_manager',
  'aprimo',
  'bynder',
  'canto',
  'kadanza_dam',
  'keepeek',
  'sitecore_content_hub',
  'demo_dam',
] as const;

export type Platform = (typeof PLATFORMS)[number];

export type PlatformKind = 'ad_platform' | 'dam';

/**
 * How a destination becomes usable.
 *
 * `oauth` sends the admin through a consent screen. `instant` needs no
 * credentials at all — enabling it is the whole setup.
 */
export type ConnectionMode = 'oauth' | 'instant';

export interface PlatformInfo {
  label: string;
  kind: PlatformKind;
  connectionMode: ConnectionMode;
  /**
   * Published by the GraFx connector itself, so the logo here is the one a user
   * already sees against that system in Studio. Null where the connector ships
   * no icon.
   */
  iconUrl: string | null;
}

const CONNECTOR_CDN = 'https://studio-cdn.chiligrafx.com/shared/demo-connectors';

export const PLATFORM_CATALOG: Record<Platform, PlatformInfo> = {
  google_ads: {
    label: 'Google Ads',
    kind: 'ad_platform',
    connectionMode: 'oauth',
    iconUrl: null,
  },
  meta: {
    label: 'Meta',
    kind: 'ad_platform',
    connectionMode: 'oauth',
    iconUrl: null,
  },
  trade_desk: {
    label: 'The Trade Desk',
    kind: 'ad_platform',
    connectionMode: 'oauth',
    iconUrl: null,
  },
  acquia_dam: {
    label: 'Acquia DAM',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: `${CONNECTOR_CDN}/Acquia_logo.svg`,
  },
  adobe_experience_manager: {
    label: 'Adobe Experience Manager',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: `${CONNECTOR_CDN}/aem_logo.svg`,
  },
  aprimo: {
    label: 'Aprimo',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: `${CONNECTOR_CDN}/Aprimo_logo.svg`,
  },
  bynder: {
    label: 'Bynder',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: 'https://asset.brandfetch.io/idRKLWV20c/id953RdnbB.svg',
  },
  canto: {
    label: 'Canto',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: `${CONNECTOR_CDN}/Canto_logo.svg`,
  },
  kadanza_dam: {
    label: 'Kadanza',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl:
      'https://s3.eu-central-1.amazonaws.com/static.kadanza.io/logo/Logo_Kadanza_GraFx_Connector_Icon.svg',
  },
  keepeek: {
    label: 'Keepeek',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: 'https://graphique-alliance.com/wp-content/uploads/2020/05/Logo_Keepeek_2.svg',
  },
  sitecore_content_hub: {
    label: 'Sitecore Content Hub',
    kind: 'dam',
    connectionMode: 'oauth',
    iconUrl: `${CONNECTOR_CDN}/Sitecore_logo.svg`,
  },
  demo_dam: {
    label: 'Your DAM system',
    kind: 'dam',
    connectionMode: 'instant',
    iconUrl: null,
  },
};

export function isPlatform(value: unknown): value is Platform {
  return typeof value === 'string' && (PLATFORMS as readonly string[]).includes(value);
}

export const PLATFORM_LABELS: Record<Platform, string> = Object.fromEntries(
  PLATFORMS.map((platform) => [platform, PLATFORM_CATALOG[platform].label]),
) as Record<Platform, string>;

export function platformsOfKind(kind: PlatformKind): Platform[] {
  return PLATFORMS.filter((platform) => PLATFORM_CATALOG[platform].kind === kind);
}
